import React, { useEffect, useMemo, useState } from 'react';

import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Box,
  Typography,
} from '@material-ui/core';
import * as GeoJSON from 'geojson';

import markerIcon from '@vyce/core/src/assets/svg/marker.svg';
import { MAIN_CONTAINER_ID } from '@vyce/core/src/constants';
import { MapView } from '@vyce/core/src/views/map/MapView';
import { LocationAreaShapePreview } from '@vyce/core/src/views/time/components/LocationAreaShapePreview';
import { MapMarker } from '@vyce/core/src/types';
import { getLocationsWithGeoRequest } from '@vyce/core/src/api/time';
import { LocationWithGeo } from '@vyce/core/src/types';
import {
  convertPointToLatLng,
  convertStringPolygonToCoordinatesArray,
  getPolygonCenter,
} from '@vyce/core/src/modules/timeModule/utils';
import { LatLngLiteral } from 'leaflet';
import { LONDON_POSITION } from '@vyce/core/src/views/map/constants';

interface Props {
  open: boolean;
  onClose: () => void;
  locationId: string;
  locationName: string;
  companyId: string;
}

const getLocationCenter = (location: LocationWithGeo): LatLngLiteral | null => {
  if (location.geo_point) {
    return convertPointToLatLng(location.geo_point);
  }
  if (location.geo_polygon) {
    const coordinates = convertStringPolygonToCoordinatesArray(location.geo_polygon);
    return getPolygonCenter(coordinates);
  }
  return null;
};

export const LocationMapDialog: React.FC<Props> = ({
  open,
  onClose,
  locationId,
  locationName,
  companyId,
}) => {
  const [locationWithGeo, setLocationWithGeo] = useState<LocationWithGeo | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !companyId) return;

    setLoading(true);
    getLocationsWithGeoRequest(companyId)
      .then(res => {
        const loc = (res.data as LocationWithGeo[]).find(l => l.uuid === locationId);
        setLocationWithGeo(loc ?? null);
      })
      .catch(() => setLocationWithGeo(null))
      .finally(() => setLoading(false));
  }, [open, companyId, locationId]);

  const center = useMemo(() => {
    if (!locationWithGeo) return LONDON_POSITION;
    return getLocationCenter(locationWithGeo) ?? LONDON_POSITION;
  }, [locationWithGeo]);

  const polygon: GeoJSON.Polygon | null = useMemo(() => {
    if (!locationWithGeo?.geo_polygon) return null;
    const coords = convertStringPolygonToCoordinatesArray(locationWithGeo.geo_polygon);
    if (coords.length < 3) return null;
    return {
      type: 'Polygon',
      coordinates: [coords],
    };
  }, [locationWithGeo]);

  const markers: MapMarker[] = useMemo(() => {
    if (!locationWithGeo || polygon) return [];
    const coords = getLocationCenter(locationWithGeo);
    if (!coords) return [];
    return [
      {
        id: locationWithGeo.uuid,
        name: locationWithGeo.name,
        lat: coords.lat,
        lon: coords.lng,
        picture: markerIcon,
      },
    ];
  }, [locationWithGeo, polygon]);

  const hasCoordinates = locationWithGeo && !!getLocationCenter(locationWithGeo);

  return (
    <Dialog
      container={document.getElementById(MAIN_CONTAINER_ID)}
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      aria-labelledby="location-map-dialog-title"
    >
      <DialogTitle id="location-map-dialog-title">{locationName}</DialogTitle>
      <DialogContent style={{ minHeight: 400 }}>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" height="400px">
            <CircularProgress />
          </Box>
        ) : !hasCoordinates ? (
          <Typography color="textSecondary">Location coordinates not available</Typography>
        ) : (
          <MapView
            height="400px"
            zoom={13}
            clustered={false}
            centerPosition={center}
            markers={markers}
            onMarkerClick={() => {}}
          >
            {polygon ? <LocationAreaShapePreview polygon={polygon} centerPosition={center} /> : undefined}
          </MapView>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="outlined">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};
